import { Node, Edge } from 'reactflow';
import { supabase } from './supabase';

interface Flow {
  id: string;
  instance_id: string;
  name: string;
  configuration: {
    nodes: Node[];
    edges: Edge[];
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function getFlow(flowId: string): Promise<Flow | null> {
  try {
    const { data, error } = await supabase
      .from('chatbots')
      .select('*')
      .eq('id', flowId)
      .single();

    if (error) {
      console.error('Error fetching flow:', error);
      throw new Error('Error al obtener el flujo: ' + error.message);
    }

    return data;
  } catch (error) {
    console.error('Error in getFlow:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Error al obtener el flujo');
  }
}

export async function saveFlow(
  instanceId: string,
  name: string,
  nodes: Node[],
  edges: Edge[]
): Promise<Flow> {
  try {
    if (!instanceId) throw new Error('ID de instancia requerido');
    if (!name) throw new Error('Nombre del flujo requerido');
    if (!nodes.length) throw new Error('El flujo debe tener al menos un nodo');

    // Verificar que existe exactamente un nodo trigger
    const triggerNodes = nodes.filter(n => n.type === 'trigger');
    if (triggerNodes.length !== 1) {
      throw new Error('El flujo debe tener exactamente un nodo trigger');
    }

    const { data: existingFlow, error: checkError } = await supabase
      .from('chatbots')
      .select('id')
      .eq('instance_id', instanceId)
      .limit(1)
      .maybeSingle();

    if (checkError) {
      console.error('Error al verificar flujo existente:', checkError);
      throw new Error('Error al verificar flujo existente: ' + checkError.message);
    }

    if (existingFlow) {
      throw new Error('Esta instancia ya tiene un flujo configurado. Solo se permite un flujo por instancia.');
    }

    // Procesar nodos para asegurar conexiones correctas
    const processedNodes = nodes.map(node => {
      if (node.type === 'trigger') {
        // Asegurar que el nodo trigger tenga los campos necesarios
        return {
          ...node,
          data: {
            ...node.data,
            isActive: true,
            startedFromTrigger: true
          }
        };
      }
      return node;
    });

    const { data, error: insertError } = await supabase
      .from('chatbots')
      .insert({
        instance_id: instanceId,
        name,
        configuration: {
          nodes: processedNodes,
          edges
        },
        is_active: true
      })
      .select()
      .single();

    if (insertError) {
      console.error('Error al guardar flujo:', insertError);
      throw new Error('Error al guardar el flujo: ' + insertError.message);
    }

    if (!data) {
      throw new Error('No se pudo crear el flujo');
    }

    return data;
  } catch (error) {
    console.error('Error en saveFlow:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Error al guardar el flujo');
  }
}

export async function updateFlow(
  flowId: string,
  name: string,
  nodes: Node[],
  edges: Edge[]
): Promise<Flow> {
  try {
    if (!flowId) throw new Error('ID de flujo requerido');
    if (!name) throw new Error('Nombre del flujo requerido');
    if (!nodes.length) throw new Error('El flujo debe tener al menos un nodo');

    // Verificar que existe exactamente un nodo trigger
    const triggerNodes = nodes.filter(n => n.type === 'trigger');
    if (triggerNodes.length !== 1) {
      throw new Error('El flujo debe tener exactamente un nodo trigger');
    }

    const { data, error } = await supabase
      .from('chatbots')
      .update({
        name,
        configuration: {
          nodes,
          edges
        }
      })
      .eq('id', flowId)
      .select()
      .single();

    if (error) {
      console.error('Error al actualizar flujo:', error);
      throw new Error('Error al actualizar el flujo: ' + error.message);
    }

    if (!data) {
      throw new Error('No se pudo actualizar el flujo');
    }

    return data;
  } catch (error) {
    console.error('Error en updateFlow:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Error al actualizar el flujo');
  }
}

export async function deleteFlow(flowId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('chatbots')
      .delete()
      .eq('id', flowId);

    if (error) {
      console.error('Error al eliminar flujo:', error);
      throw new Error('Error al eliminar el flujo: ' + error.message);
    }
  } catch (error) {
    console.error('Error en deleteFlow:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Error al eliminar el flujo');
  }
}

export async function deleteAllFlows(instanceId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('chatbots')
      .delete()
      .eq('instance_id', instanceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting all flows:', error);
    throw error;
  }
}

export async function activateFlow(instanceId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('chatbots')
      .update({ is_active: true })
      .eq('instance_id', instanceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error activating flow:', error);
    throw error;
  }
}

export async function deactivateFlow(instanceId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('chatbots')
      .update({ is_active: false })
      .eq('instance_id', instanceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deactivating flow:', error);
    throw error;
  }
}

export async function resetAllConversations(instanceId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('chatbot_conversations')
      .delete()
      .eq('instance_id', instanceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error resetting conversations:', error);
    throw error;
  }
}